import { CONTROLLER, METHOD } from "common/config/endpoint.config";
import { ApiConfig } from "common/models";
import { buildEngApiUrl, buildGdxApiUrl } from "common/utils/api.util";
import {
  SearchDocumentsRequest,
  UploadRequest,
  MultiUploadRequest,
  DocumentActionRequest
} from "../models/processing-hub.model";

export const processingHubConfig = {
  getDocuments: (searchParams: SearchDocumentsRequest) => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.DOCUMENTS}/${METHOD.LIST}`),
      "POST",
      "",
      searchParams
    );
  },
  exportExtractedDataExcel: (documentId: string | number) => {
    return new ApiConfig(
      buildGdxApiUrl(`/${CONTROLLER.GDX_DATABASE}/export-extracted-data-excel`),
      "POST",
      "",
      { documentId }
    );
  },

  getUploadedDocuments: (
    hasDuplicate = false,
    paginationParams?: {
      page?: number;
      size?: number;
      sort?: string;
      dir?: string;
      search?: string;
      showDuplicates?: boolean;
      useEngApi?: boolean;
      flowId?: number | string;
    }
  ) => {
    const payload = {
      page: paginationParams?.page || 1,
      size: paginationParams?.size || 10,
      sort: paginationParams?.sort || "",
      dir: paginationParams?.dir || "",
      search: paginationParams?.search || "",
      showDuplicates: paginationParams?.showDuplicates || false,
      ...(paginationParams?.flowId !== undefined &&
      paginationParams?.flowId !== null
        ? { flowId: paginationParams.flowId }
        : {})
    };

    const baseUrlBuilder = paginationParams?.useEngApi
      ? buildEngApiUrl
      : buildGdxApiUrl;

    return new ApiConfig(
      baseUrlBuilder(`${CONTROLLER.ADMIN}/${CONTROLLER.DOCUMENTS}`),
      "POST",
      `hasDuplicate=${false}`,
      payload
    );
  },

  uploadDocuments: (uploadRequest: UploadRequest): ApiConfig => {
    const isEngineeringFlow =
      uploadRequest.documentType?.toLowerCase() === "engineering";
    const referenceType = uploadRequest.referenceType || "GP";

    if (isEngineeringFlow) {
      const formData =
        uploadRequest.files instanceof FormData
          ? uploadRequest.files
          : (() => {
              const data = new FormData();
              uploadRequest.files.forEach((file) =>
                data.append("files", file, file.name)
              );
              return data;
            })();

      if (uploadRequest.gpName && !formData.has("gpName")) {
        formData.append("gpName", uploadRequest.gpName);
      }

      if (
        uploadRequest.isReferenceDocument !== undefined &&
        !formData.has("isReferenceDocument")
      ) {
        formData.append(
          "isReferenceDocument",
          uploadRequest.isReferenceDocument.toString()
        );
      }

      if (!formData.has("referenceType")) {
        formData.append("referenceType", referenceType);
      }

      if (uploadRequest.flowId && !formData.has("flowId")) {
        formData.append("flowId", uploadRequest.flowId.toString());
      }

      if (uploadRequest.fileCategory && !formData.has("file_category")) {
        formData.append("file_category", uploadRequest.fileCategory);
      }

      if (!formData.has("batchId")) {
        formData.append("batchId", "");
      }

      return new ApiConfig(
        buildEngApiUrl(`${CONTROLLER.DOCUMENTS}/${METHOD.UPLOAD}`),
        "POST",
        "",
        formData
      );
    }

    // Build query parameters
    const queryParams = new URLSearchParams();

    if (uploadRequest.isReferenceDocument !== undefined) {
      queryParams.append(
        "isReferenceDocument",
        uploadRequest.isReferenceDocument.toString()
      );
    }

    if (uploadRequest.gpName) {
      queryParams.append("gpName", uploadRequest.gpName);
    }

    queryParams.append("referenceType", referenceType);

    // Add flowId if provided
    if (uploadRequest.flowId) {
      queryParams.append("flowId", uploadRequest.flowId.toString());
    }

    const queryString = queryParams.toString();

    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.DOCUMENTS}/${METHOD.UPLOAD}`),
      "POST",
      queryString ? `${queryString}` : "",
      uploadRequest.files // Pass files directly as FormData
    );
  },

  multiUploadDocuments: (uploadRequest: MultiUploadRequest): ApiConfig => {
    const isEngineeringFlow =
      uploadRequest.documentType?.toLowerCase() === "engineering";
    const gpName = uploadRequest.gpName || "BULKUPLOAD";
    const isReferenceDocument = uploadRequest.isReferenceDocument ?? false;
    const referenceType = uploadRequest.referenceType || "GP";

    if (isEngineeringFlow) {
      const formData =
        uploadRequest.files instanceof FormData
          ? uploadRequest.files
          : (() => {
              const data = new FormData();
              uploadRequest.files.forEach((file) =>
                data.append("files", file, file.name)
              );
              return data;
            })();

      if (!formData.has("gpName")) {
        formData.append("gpName", gpName);
      }

      if (!formData.has("isReferenceDocument")) {
        formData.append("isReferenceDocument", isReferenceDocument.toString());
      }

      if (!formData.has("referenceType")) {
        formData.append("referenceType", referenceType);
      }

      if (uploadRequest.flowId && !formData.has("flowId")) {
        formData.append("flowId", uploadRequest.flowId.toString());
      }

      if (uploadRequest.fileCategory && !formData.has("file_category")) {
        formData.append("file_category", uploadRequest.fileCategory);
      }

      if (!formData.has("batchId")) {
        formData.append("batchId", "");
      }

      return new ApiConfig(
        buildEngApiUrl(`${CONTROLLER.DOCUMENTS}/${METHOD.UPLOAD}`),
        "POST",
        "",
        formData
      );
    }

    // Build query parameters with default values
    const queryParams = new URLSearchParams();

    // Set default values for multi-upload
    queryParams.append("gpName", gpName);
    queryParams.append("isReferenceDocument", isReferenceDocument.toString());
    queryParams.append("referenceType", referenceType);

    // Add flowId if provided
    if (uploadRequest.flowId) {
      queryParams.append("flowId", uploadRequest.flowId.toString());
    }

    if (uploadRequest.fileCategory) {
      queryParams.append("file_category", uploadRequest.fileCategory);
    }

    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.DOCUMENTS}/${METHOD.UPLOAD}`),
      "POST",
      queryParams.toString(),
      uploadRequest.files // Pass files directly as FormData
    );
  },

  getProcessingAssignments: (documentType?: string) => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.DOCUMENTS}/assignments`),
      "GET",
      documentType ? `?documentType=${documentType}` : "",
      undefined
    );
  },

  deleteDocument: (documentId: string | number): ApiConfig => {
    return new ApiConfig(
      buildEngApiUrl(`${CONTROLLER.DOCUMENTS}/${documentId}`),
      "DELETE",
      undefined,
      undefined
    );
  },

  deleteEngineeringDocument: (documentId: string | number): ApiConfig => {
    return new ApiConfig(
      buildEngApiUrl(`${CONTROLLER.DOCUMENTS}/${documentId}`),
      "DELETE",
      undefined,
      undefined
    );
  },

  deleteGDXDocument: (
    documentId: string | number,
    flowId: string | number
  ): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(
        `${CONTROLLER.GDX_DATABASE}/document-details/soft-delete/${flowId}/${documentId}`
      ),
      "DELETE",
      undefined,
      undefined
    );
  },

  processDocument: (actionRequest: DocumentActionRequest): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(
        `${CONTROLLER.DOCUMENTS}/${actionRequest.documentId}/${actionRequest.action}`
      ),
      "POST",
      undefined,
      actionRequest
    );
  },

  refreshDocuments: () => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.DOCUMENTS}/refresh`),
      "POST",
      "",
      undefined
    );
  },

  getDocumentTypeInformation: (documentType: string) => {
    return new ApiConfig(
      buildGdxApiUrl(
        `${CONTROLLER.GDX_DATABASE}/document_type_information/get/${documentType}`
      ),
      "GET",
      "",
      undefined
    );
  },

  getGdxDocuments: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_DATABASE}/main/get-all`),
      "GET",
      "",
      undefined
    );
  },

  getGdxDocumentsPaginated: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_DATABASE}/document-master/get-all`),
      "POST",
      "",
      undefined
    );
  },

  getFlowConfigurations: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_DATABASE}/flowconfig/get-all`),
      "GET",
      "",
      undefined
    );
  },

  getFlowConfigurationById: (configId: string | number): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_DATABASE}/flowconfig/get/${configId}`),
      "GET",
      "",
      undefined
    );
  },

  getConfigurationDetails: (configId: string | number): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_DATABASE}/config/get/${configId}`),
      "GET",
      "",
      undefined
    );
  },

  batchUpload: (uploadRequest: any): ApiConfig => {
    const formData = new FormData();

    // Append files
    uploadRequest.files.forEach((file: File) => {
      formData.append("files", file, file.name);
    });

    // Append required parameters
    formData.append("flow_id", uploadRequest.flow_id.toString());
    formData.append("model_name", uploadRequest.model_name);
    formData.append("chat_enabled", uploadRequest.chat_enabled.toString());

    // Append optional parameters with defaults
    formData.append("batch_size", (uploadRequest.batch_size || 10).toString());
    formData.append(
      "batch_status",
      uploadRequest.batch_status || "In Progress"
    );

    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_BATCH_UPLOAD}/batch-upload`),
      "POST",
      "",
      formData
    );
  },

  getGdxDocumentDetail: (
    flowId: string | number,
    documentId: string | number
  ): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(
        `${CONTROLLER.GDX_DATABASE}/extracted-data/doctype-view/${flowId}/${documentId}`
      ),
      "GET",
      "",
      undefined
    );
  },

  createBatch: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_DATABASE}/batch/create`),
      "POST",
      "",
      undefined
    );
  },

  getAllBatches: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_DATABASE}/batch/get-all`),
      "POST",
      "",
      undefined
    );
  },

  updateGdxDocument: (
    flowId: string | number,
    docId: string | number
  ): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(
        `${CONTROLLER.GDX_DATABASE}/document-master/update/${flowId}/${docId}`
      ),
      "PUT",
      "",
      undefined
    );
  },

  getMarketingViewDistinctValues: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(
        `${CONTROLLER.GDX_DATABASE}/marketing-view/distinct-values`
      ),
      "POST",
      "",
      undefined
    );
  },

  getMarketingView: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX_DATABASE}/marketing-view/`),
      "POST",
      "",
      undefined
    );
  },

  generateExcel: (): ApiConfig => {
    return new ApiConfig(
      buildGdxApiUrl(`${CONTROLLER.GDX}/dashboard/generate-excel`),
      "POST",
      "",
      undefined
    );
  }
};
